import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Typography, Divider, ButtonGroup, Button } from "@mui/material";
import { useGetMemberActivityFeed } from "services/feed";
import { AccountStub } from "protogen/common_pb";
import { FeedEntry, FeedEntryType } from "protogen/feed_pb";
import { TaskChangeEventType } from "protogen/tasks_pb";
import Breadcrumbs from "components/common/Breadcrumbs";
import useTitle from "components/hooks/useTitle";
import FlexPage from "components/layout/FlexPage";
import useIsMobile from "components/hooks/useIsMobile";
import { DefaultAvatar, UserAvatar } from "components/common/CurrentUserAvatar";
import DateDisplay from "components/common/DateDisplay";
import LinkRouter from "components/navigation/LinkRouter";
import { StageChip } from "components/tasks/workflowStates";
import FlexBox from "components/common/FlexBox";
import { CurrentUserContext } from "components/context/RequireAuth";
import {
  getActivityTitle,
  getActivityLink,
  getActivityDescription,
  getCommentPreview,
  combineEntries,
  groupEntriesByActorAndType,
  getActvityByline,
} from "components/member/home/activityFeedUtils";
import TruncatedText from "components/common/TruncatedText";

import { EmailMessage } from "protogen/conversation_pb";
import { Calendar, CheckCircle, File, Mail, Mailbox } from "lucide-react";
import Loading from "components/common/Loading";
import { useWebSocketContext } from "components/context/SocketConsumerProvider";
import useIsVisible from "components/hooks/useIsVisible";
import { useMemberDetails } from "components/context/MemberDetailsContext";

const getEmailIcon = (emailMessage: EmailMessage) => {
  switch (emailMessage.emailType) {
    case "tasks":
      return <CheckCircle size={24.25} />;
    case "events":
      return <Calendar size={24.25} />;
    case "docs":
      return <File size={24.5} />;
    case "family-alias":
      return <Mailbox size={24.5} />;
    default:
      return <Mail size={24.5} />;
  }
};

type TimePeriod = "Today" | "Yesterday" | "Last week" | "Last month" | "Older";

const getTimePeriod = (date: Date): TimePeriod => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);
  const lastMonth = new Date(today);
  lastMonth.setDate(lastMonth.getDate() - 30);

  if (date >= today) return "Today";
  if (date >= yesterday) return "Yesterday";
  if (date >= lastWeek) return "Last week";
  if (date >= lastMonth) return "Last month";
  return "Older";
};

const groupEntriesByTimePeriod = (
  entries: FeedEntry[],
): Record<TimePeriod, FeedEntry[]> => {
  const groups: Record<TimePeriod, FeedEntry[]> = {
    Today: [],
    Yesterday: [],
    "Last week": [],
    "Last month": [],
    Older: [],
  };

  entries.forEach((entry) => {
    const date = new Date(Number(entry.timestampSec) * 1000);
    const period = getTimePeriod(date);
    groups[period].push(entry);
  });

  return groups;
};

const ActivityEntryItem = ({
  entry,
  accountMap,
  currentUserRef,
  isLastEntry,
}: {
  entry: FeedEntry;
  accountMap: Record<string, AccountStub>;
  currentUserRef?: string;
  isLastEntry: boolean;
}) => {
  const isCurrentUser = entry.senderRef === currentUserRef;
  const actor = accountMap[entry.senderRef];
  const actorName = actor?.firstName ? `${actor.firstName}` : "User";

  const displayName = isCurrentUser ? "You" : actorName;

  const description = getActivityDescription(entry, displayName);
  const activityTitle = getActivityTitle(entry);
  const activityLink = getActivityLink(entry);
  const commentPreview = getCommentPreview(entry);
  const isMobile = useIsMobile();
  const activityByline = getActvityByline(entry);

  return (
    <>
      <Box sx={{ display: "flex", gap: "12px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            flex: 1,
            minWidth: 0,
          }}
        >
          <FlexBox justifyContent="space-between">
            <FlexBox alignItems="center" gap="8px">
              {entry.taskChangeEvent?.type ===
              TaskChangeEventType.TASK_CHANGE_EVENT_STAGE_CHANGED ? (
                <Typography
                  variant="bodyHeavy"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  {displayName}{" "}
                  {entry.taskChangeEvent?.newStage === "done"
                    ? "completed a task"
                    : "updated a task"}
                </Typography>
              ) : (
                <Typography variant="bodyHeavy">{description}</Typography>
              )}
            </FlexBox>
            <DateDisplay date={new Date(Number(entry.timestampSec) * 1000)} />
          </FlexBox>

          <FlexBox
            stackedMobile
            gap={isMobile ? "2px" : "8px"}
            alignItems={isMobile ? "start" : "center"}
            sx={isMobile ? { width: "100%" } : {}}
          >
            <LinkRouter
              to={activityLink}
              sx={{
                padding: 0,
                display: "flex",
                minWidth: 0,
                gap: "4px",
                ...(isMobile && { width: "100%" }),
              }}
            >
              <TruncatedText
                variant="bodyHeavy"
                color="primary.main"
                sx={{
                  ...(isMobile && { width: "100%" }),
                }}
              >
                {activityTitle}
              </TruncatedText>
            </LinkRouter>
            {isMobile && commentPreview && (
              <TruncatedText
                variant="body"
                color="text.secondary"
                sx={{ width: "100%" }}
              >
                {commentPreview}
              </TruncatedText>
            )}
            {entry.taskChangeEvent?.type ===
              TaskChangeEventType.TASK_CHANGE_EVENT_STAGE_CHANGED && (
              <StageChip
                stage={entry.taskChangeEvent.newStage}
                accountType="member"
                size="small"
                sx={{ marginTop: isMobile ? 0 : "2px" }}
              />
            )}
          </FlexBox>

          {!isMobile && commentPreview && (
            <TruncatedText variant="body" color="text.secondary">
              {commentPreview}
            </TruncatedText>
          )}
          {activityByline && (
            <Typography variant="body" color="text.secondary">
              {activityByline}
            </Typography>
          )}
        </Box>
      </Box>
      {!isLastEntry && <Divider sx={{ width: "100%" }} />}
    </>
  );
};

export default () => {
  useTitle("Activity");
  const { request: getActivityFeed } = useGetMemberActivityFeed();
  const [entries, setEntries] = useState<FeedEntry[]>([]);
  const [accountMap, setAccountMap] = useState<Record<string, AccountStub>>({});
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [emailFilter, setEmailFilter] = useState(() => {
    return searchParams.get("emailFilter") === "true";
  });
  const { isDigitalSubscription } = useMemberDetails();
  const isMobile = useIsMobile();
  const hideAvatars = isDigitalSubscription;

  const { registerHandler, unregisterHandler } = useWebSocketContext();
  const { isVisible } = useIsVisible({});

  useEffect(() => {
    const pushHandler = () => {
      if (isVisible) {
        fetchData();
      }
    };
    registerHandler(["email/", "task/"], pushHandler);
    return () => {
      unregisterHandler(["email/", "task/"], pushHandler);
    };
  }, [isVisible]);

  useEffect(() => {
    fetchData();
  }, [emailFilter]);

  const fetchData = async () => {
    setLoading(true);

    // Get activity feed
    const response = await getActivityFeed({
      startCursor: "",
      markConversationsRead: false,
      pageSize: 50,
      direction: "desc",
      entryTypes: emailFilter ? [FeedEntryType.EMAIL_ACTION_ALIAS] : [],
    });

    if (response) {
      const filteredEntries = response.entries.filter((entry) => {
        const activityTitle = getActivityTitle(entry);
        return activityTitle !== "Unknown task";
      });
      setEntries(filteredEntries);

      const accountsMap: Record<string, AccountStub> = {};
      response.accounts.forEach((account) => {
        accountsMap[account.ref] = account;
      });

      setAccountMap(accountsMap);
    }
    setLoading(false);
  };

  const currentUser = useContext(CurrentUserContext);

  return (
    <FlexPage leftAligned sx={{ width: "100%" }}>
      <Breadcrumbs
        breadcrumbs={[{ name: "Home", link: "/" }, { name: "Activity" }]}
      />
      <Typography variant="display" sx={{ marginBottom: "16px" }}>
        Family activity
      </Typography>

      <FlexBox direction="column">
        <Box>
          <ButtonGroup
            sx={{
              width: "fit-content",
              ...(isMobile && {
                marginBottom: "16px",
                flex: "unset",
              }),
            }}
          >
            <Button
              variant={emailFilter ? "buttonGroup" : "buttonGroupSelected"}
              onClick={() => {
                setEmailFilter(false);
                setSearchParams((prev) => {
                  const newParams = new URLSearchParams(prev);
                  newParams.delete("emailFilter");
                  return newParams;
                });
              }}
            >
              All
            </Button>
            <Button
              variant={emailFilter ? "buttonGroupSelected" : "buttonGroup"}
              onClick={() => {
                setEmailFilter(true);
                setSearchParams((prev) => {
                  const newParams = new URLSearchParams(prev);
                  newParams.set("emailFilter", "true");
                  return newParams;
                });
              }}
            >
              Emails
            </Button>
          </ButtonGroup>
        </Box>
        {loading ? (
          <Box sx={{ textAlign: "center" }}>
            <Loading />
          </Box>
        ) : entries.length === 0 ? (
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="bodyHeavy">No recent activity</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              gap: isMobile ? "16px" : "20px",
              flexDirection: "column",
            }}
          >
            {(() => {
              const timeGroups = groupEntriesByTimePeriod(entries);
              const periods: TimePeriod[] = [
                "Today",
                "Yesterday",
                "Last week",
                "Last month",
                "Older",
              ];

              return periods.map((period) => {
                const periodEntries = timeGroups[period];
                if (periodEntries.length === 0) return null;

                const combinedEntries = combineEntries(periodEntries);
                const groupedEntries =
                  groupEntriesByActorAndType(combinedEntries);
                return (
                  <Box key={period}>
                    {period !== "Older" && (
                      <Typography
                        variant="body"
                        sx={{
                          color: "text.secondary",
                          marginBottom: isMobile ? "12px" : "16px",
                        }}
                      >
                        {period}
                      </Typography>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        gap: isMobile ? "16px" : "20px",
                        flexDirection: "column",
                      }}
                    >
                      {groupedEntries.map((group, groupIndex) => {
                        const firstEntry = group.entries[0];
                        const isEmailGroup = !!firstEntry?.emailMessage;
                        const isEmailAlias =
                          isEmailGroup &&
                          !(firstEntry?.emailMessage?.emailType === "advisor");
                        const groupActor = accountMap[group.actorRef];
                        return (
                          <FlexBox
                            alignItems="start"
                            key={`group-${group.actorRef}-${groupIndex}`}
                            gap={isMobile ? "8px" : "12px"}
                          >
                            {!hideAvatars && (
                              <Box
                                sx={{
                                  flexShrink: 0,
                                  width: isMobile ? 32 : 40,
                                  height: isMobile ? 32 : 40,
                                  marginTop: "4px",
                                  ...(isEmailGroup && {
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "50%",
                                    backgroundColor: "background.paper",
                                    border: "1px solid",
                                    borderColor: "divider",
                                  }),
                                }}
                              >
                                {isEmailAlias ? (
                                  getEmailIcon(firstEntry?.emailMessage!)
                                ) : groupActor ? (
                                  <UserAvatar
                                    user={groupActor}
                                    size={isMobile ? 32 : 40}
                                  />
                                ) : group.actorRef ? (
                                  <DefaultAvatar size={isMobile ? 32 : 40} />
                                ) : null}
                              </Box>
                            )}
                            <FlexBox
                              variant="contained"
                              direction="column"
                              sx={{
                                padding: isMobile ? "16px" : "16px 20px",
                                minWidth: 0,
                              }}
                            >
                              {group.entries.map((entry, entryIndex) => (
                                <FlexBox
                                  direction="column"
                                  key={`${entry.ref}-${entryIndex}`}
                                >
                                  <ActivityEntryItem
                                    entry={entry}
                                    accountMap={accountMap}
                                    currentUserRef={currentUser?.ref}
                                    isLastEntry={
                                      entryIndex === group.entries.length - 1
                                    }
                                  />
                                </FlexBox>
                              ))}
                            </FlexBox>
                          </FlexBox>
                        );
                      })}
                    </Box>
                  </Box>
                );
              });
            })()}
          </Box>
        )}
      </FlexBox>
    </FlexPage>
  );
};
